import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { LISTING_STATUSES } from "../lib/phases";
import { Switch } from "../components/ui/switch";
import { Mail } from "lucide-react";

const EMPTY = {
  property_address: "",
  seller_names: "",
  seller_email: "",
  seller_2_name: "",
  seller_2_email: "",
  cc_email: "",
  listing_agent: "",
  target_list_date: "",
  closing_date: "",
  mls_number: "",
  listing_status: "Pre-Listing",
  general_notes: "",
  seller_daily_email: true,
  escrow_company: "",
  escrow_officer_name: "",
  escrow_officer_email: "",
  escrow_officer_phone: "",
  escrow_number: "",
};

export default function CreateListingDialog({ open, onOpenChange, onCreated, initial, listingId }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial, target_list_date: initial.target_list_date || "" } : EMPTY);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        target_list_date: form.target_list_date || null,
        closing_date: form.closing_date || null,
      };
      const { data } = listingId
        ? await api.put(`/listings/${listingId}`, payload)
        : await api.post("/listings", payload);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-listing-dialog" className="sm:max-w-2xl bg-[#faf7f2]">
        <DialogHeader>
          <p className="overline text-stone-500 mb-1">
            {listingId ? "Edit" : "New Home"}
          </p>
          <DialogTitle className="font-heading font-semibold tracking-tight text-2xl text-stone-900">
            {listingId ? "Refine home details" : "Set up the home"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            The essentials so we can price it right, market it beautifully, and close it smoothly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="property_address" className="text-stone-700">Property address</Label>
            <Input
              id="property_address"
              data-testid="listing-property-address"
              value={form.property_address}
              onChange={set("property_address")}
              placeholder="123 Elm Street, Austin, TX"
              required
              className="h-10 border-stone-300 bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="seller_names" className="text-stone-700">Seller name(s)</Label>
            <Input
              id="seller_names"
              data-testid="listing-seller-names"
              value={form.seller_names}
              onChange={set("seller_names")}
              placeholder="Jane & John Doe"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="seller_email" className="text-stone-700">Seller email</Label>
            <Input
              id="seller_email"
              data-testid="listing-seller-email"
              type="email"
              value={form.seller_email}
              onChange={set("seller_email")}
              placeholder="seller@email.com"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="seller_2_name" className="text-stone-700">
              Seller 2 name <span className="text-stone-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="seller_2_name"
              data-testid="listing-seller-2-name"
              value={form.seller_2_name}
              onChange={set("seller_2_name")}
              placeholder="e.g. Jamie Doe"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="seller_2_email" className="text-stone-700">
              Seller 2 email <span className="text-stone-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="seller_2_email"
              data-testid="listing-seller-2-email"
              type="email"
              value={form.seller_2_email}
              onChange={set("seller_2_email")}
              placeholder="second-seller@email.com"
              className="h-10 border-stone-300 bg-white"
            />
            <p className="text-[11px] text-stone-500 leading-relaxed">
              Both primary and secondary seller emails receive the automated daily progress digest.
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cc_email" className="text-stone-700">
              CC email <span className="text-stone-400 font-normal">(e.g., Transaction Coordinator)</span>
            </Label>
            <Input
              id="cc_email"
              data-testid="listing-cc-email"
              type="email"
              value={form.cc_email}
              onChange={set("cc_email")}
              placeholder="tc@brokerage.com"
              className="h-10 border-stone-300 bg-white"
            />
            <p className="text-[11px] text-stone-500 leading-relaxed">
              This address will be copied on the daily seller digest email.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="listing_agent" className="text-stone-700">Listing agent</Label>
            <Input
              id="listing_agent"
              data-testid="listing-agent"
              value={form.listing_agent}
              onChange={set("listing_agent")}
              placeholder="Alex Rivera"
              className="h-10 border-stone-300 bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="target_list_date" className="text-stone-700">Target list date</Label>
            <Input
              id="target_list_date"
              data-testid="listing-target-date"
              type="date"
              value={form.target_list_date || ""}
              onChange={set("target_list_date")}
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="closing_date" className="text-stone-700">Target close date</Label>
            <Input
              id="closing_date"
              data-testid="listing-closing-date"
              type="date"
              value={form.closing_date || ""}
              onChange={set("closing_date")}
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="mls_number" className="text-stone-700">MLS number</Label>
            <Input
              id="mls_number"
              data-testid="listing-mls-number"
              value={form.mls_number}
              onChange={set("mls_number")}
              placeholder="A0000000"
              className="h-10 border-stone-300 bg-white"
            />
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="listing_status" className="text-stone-700">Home status</Label>
            <Select value={form.listing_status} onValueChange={(v) => setForm((f) => ({ ...f, listing_status: v }))}>
              <SelectTrigger data-testid="listing-status-trigger" id="listing_status" className="h-10 border-stone-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LISTING_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 pt-2 pb-1">
            <p className="overline text-stone-500">Escrow contact</p>
            <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">
              Optional. Once entered, this shows in the agent Home Info card and on the seller share portal so your sellers can reach escrow directly.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="escrow_company" className="text-stone-700">Escrow company</Label>
            <Input
              id="escrow_company"
              data-testid="listing-escrow-company"
              value={form.escrow_company}
              onChange={set("escrow_company")}
              placeholder="First American Title"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="escrow_officer_name" className="text-stone-700">Escrow officer</Label>
            <Input
              id="escrow_officer_name"
              data-testid="listing-escrow-officer-name"
              value={form.escrow_officer_name}
              onChange={set("escrow_officer_name")}
              placeholder="Jamie Perez"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="escrow_officer_email" className="text-stone-700">Escrow officer email</Label>
            <Input
              id="escrow_officer_email"
              data-testid="listing-escrow-officer-email"
              type="email"
              value={form.escrow_officer_email}
              onChange={set("escrow_officer_email")}
              placeholder="jamie@firstam.com"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="escrow_officer_phone" className="text-stone-700">Escrow officer phone</Label>
            <Input
              id="escrow_officer_phone"
              data-testid="listing-escrow-officer-phone"
              type="tel"
              value={form.escrow_officer_phone}
              onChange={set("escrow_officer_phone")}
              placeholder="(555) 123-4567"
              className="h-10 border-stone-300 bg-white"
            />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="escrow_number" className="text-stone-700">Escrow / file number</Label>
            <Input
              id="escrow_number"
              data-testid="listing-escrow-number"
              value={form.escrow_number}
              onChange={set("escrow_number")}
              placeholder="e.g. ESC-24-1042"
              className="h-10 border-stone-300 bg-white"
            />
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="general_notes" className="text-stone-700">General notes</Label>
            <Textarea
              id="general_notes"
              data-testid="listing-notes"
              value={form.general_notes}
              onChange={set("general_notes")}
              rows={3}
              placeholder="Anything to remember about this home…"
              className="border-stone-300 bg-white"
            />
          </div>

          <div className="sm:col-span-2 flex items-start justify-between gap-4 rounded-md border border-stone-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-stone-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-stone-900">Send seller a daily progress email</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                  When any task on this home is completed, we'll email the seller at <b>6 PM Pacific</b> with today's progress. Also auto-enables the seller share link.
                </p>
              </div>
            </div>
            <Switch
              data-testid="listing-seller-daily-email"
              checked={!!form.seller_daily_email}
              onCheckedChange={(v) => setForm((f) => ({ ...f, seller_daily_email: v }))}
              className="mt-1"
            />
          </div>

          <DialogFooter className="sm:col-span-2 mt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="listing-cancel-button"
              className="rounded-full border-stone-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="listing-save-button"
              className="text-white rounded-full px-5"
              style={{ backgroundColor: "#010082" }}
            >
              {busy ? "Saving..." : listingId ? "Save changes" : "Begin workflow"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
